"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const services = [
  {
    id: "asset",
    icon: "BuildingLibraryIcon",
    number: "01",
    title: "Asset Management",
    description:
      "We help organizations manage, track, and optimize their physical and financial assets to ensure maximum return on investment and operational efficiency.",
    tags: ["ROI Optimization", "Asset Tracking", "Portfolio Analysis"],
    accent: "rgba(201,168,76,0.15)",
    bullets: null,
  },
  {
    id: "inventory",
    icon: "ArchiveBoxIcon",
    number: "02",
    title: "Inventory Management",
    description:
      "Our solutions reduce waste, improve stock visibility, and streamline inventory control systems to ensure smooth supply chain flow.",
    tags: ["Waste Reduction", "Stock Visibility", "Supply Chain"],
    accent: "rgba(100,149,237,0.12)",
    bullets: null,
  },
  {
    id: "logistics",
    icon: "TruckIcon",
    number: "03",
    title: "Logistics & Transportation",
    description:
      "From procurement to last-mile delivery, we design efficient logistics frameworks that enhance speed, reduce costs, and improve reliability.",
    tags: ["Last-Mile Delivery", "Procurement", "Cost Reduction"],
    accent: "rgba(52,211,153,0.1)",
    bullets: null,
  },
  {
    id: "consulting",
    icon: "PresentationChartLineIcon",
    number: "04",
    title: "Business Consulting & Financial Planning",
    description:
      "We provide strategic advisory services that align operational processes with financial performance, enabling sustainable business growth.",
    tags: ["Strategic Advisory", "Financial Planning", "Growth Strategy"],
    accent: "rgba(201,168,76,0.12)",
    bullets: null,
  },
  {
    id: "transactions",
    icon: "GlobeAltIcon",
    number: "05",
    title: "Global Transaction & Funding Solutions",
    description:
      "End-to-end facilitation of high-value international financial transactions and large-scale funding arrangements — from MT103 SWIFT wire transfers and institutional-grade cryptocurrency settlements to corporate capital raises and strategic investment deal structuring.",
    tags: ["MT103 SWIFT Transfers", "Crypto Settlements", "Capital Raises", "Investment Deals"],
    accent: "rgba(139,92,246,0.12)",
    bullets: [
      "MT103 SWIFT Wire Transfers",
      "High-Volume Crypto Transactions",
      "Corporate Business Funding",
      "Strategic Investment Deals",
    ],
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );

    const el = sectionRef.current;
    if (el) {
      el.querySelectorAll(".reveal, .reveal-scale").forEach((item) =>
        observer.observe(item)
      );
    }
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative py-28 bg-[#A8A8A8]"
    >
      <div className="absolute top-0 left-0 right-0 gold-line" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div className="reveal">
            <p className="text-[11px] font-display uppercase tracking-[0.2em] text-gold mb-3">
              What We Do
            </p>
            <h2 className="text-4xl md:text-5xl font-display font-700 text-[#0C1B33] leading-tight tracking-tight">
              Core Services
            </h2>
          </div>
          <p className="reveal delay-200 text-[19px] font-body font-500 text-[#0C1B33]/80 max-w-md leading-[1.75]">
            End-to-end operational solutions designed to reduce friction, cut costs, and accelerate growth at every stage of your business.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`reveal delay-${(index + 1) * 100} service-card-ash rounded-2xl p-8 group cursor-default${
                service.id === "transactions" ? " md:col-span-2" : ""
              }`}
            >
              {/* Top row */}
              <div className="flex items-start justify-between mb-6">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: service.accent }}
                >
                  <Icon
                    name={service.icon as "TruckIcon"}
                    size={22}
                    className="text-gold"
                  />
                </div>
                <span className="text-[11px] font-display font-600 text-[#0C1B33]/40 tracking-widest">
                  {service.number}
                </span>
              </div>

              {/* Content */}
              <h3 className="text-[22px] font-display font-700 text-[#0C1B33] mb-3 leading-snug tracking-tight group-hover:text-gold transition-colors duration-300">
                {service.title}
              </h3>
              <p className="text-[17px] font-body font-500 text-[#0C1B33]/80 leading-[1.75] mb-6">
                {service.description}
              </p>

              {/* Bullet points for sub-services */}
              {service.bullets && (
                <ul className="mb-6 space-y-2">
                  {service.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-center gap-2.5 text-[16px] font-body font-500 text-[#0C1B33]/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold/70 flex-shrink-0" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {service.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[11px] font-display font-500 uppercase tracking-wider px-3 py-1 rounded-full border border-[rgba(184,134,11,0.5)] text-gold/90"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 gold-line" />
    </section>
  );
}